import { memo } from "react";
import Tooltip from "@mui/material/Tooltip";
import SidebarIconRenderer from "./SidebarIcons/SidebarIconRenderer";
import { getOrganizedDataProvider, sidebarOptions, sidebarValueConstants } from "./sidebarutils";

import {
  GlobalSidebarHeaderContainer,
  GlobalSidebarIconTextContainer,
  GlobalSidebarRootContainer,
  GlobalSidebarRowContainer,
} from "./styled";
import { getFormsByFilter } from "../../Services/API";

interface GlobalSidebarProps {
  loading: boolean;
  deletedLength: number;
  draftedLength: number;
  publishedLength: number;
  selectedValue: string;
  setSelectedValue: (value: string) => void;
  hasMore: boolean;
  setDataProvider: (value: any) => void;
  setForms: (value: any) => void;
  setIsLoading?: (value: boolean) => void;
  isCollapsed: boolean;
  onToggle: () => void;
}

function GlobalSidebar({
  loading,
  deletedLength,
  draftedLength,
  publishedLength,
  selectedValue,
  setSelectedValue,
  hasMore,
  setDataProvider,
  setForms,
  setIsLoading,
  isCollapsed,
  onToggle,
}: GlobalSidebarProps) {
  const getCountBasedOnValue = (value: string) => {
    if (loading) return '';

    if (value === sidebarValueConstants.deleted) {
      return deletedLength;
    } else if (value === sidebarValueConstants.drafted) {
      return draftedLength;
    } else if (value === sidebarValueConstants.published) {
      return publishedLength;
    } else {
      return 0;
    }
  };

  const handeleSidebarClick = async (value: string) => {
    setIsLoading?.(false);
    setSelectedValue(value);
    if (hasMore) {
      const response = await getFormsByFilter(value, sessionStorage?.principalId)
      const formData = response?.data || [];
      setForms((prev: any) => {
        const combined = [...prev, ...formData];
        const uniqueCombined = combined.filter((value, index, self) => {
          return index === self.findIndex((t) => t._id === value._id);
        });
        setDataProvider(getOrganizedDataProvider(uniqueCombined));
        return uniqueCombined;
      });
    }
  }
  return (
    <GlobalSidebarRootContainer isCollapsed={isCollapsed}>
      <GlobalSidebarHeaderContainer isCollapsed={isCollapsed}>
        {!isCollapsed && <span>My Forms</span>}
        <Tooltip
          title={isCollapsed ? "Expand sidebar" : "Collapse sidebar"}
          placement="right"
          arrow
        >
          <button
            onClick={onToggle}
            aria-label={isCollapsed ? "Expand sidebar" : "Collapse sidebar"}
            style={{
              background: "none",
              border: "none",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              padding: "6px",
              borderRadius: "6px",
              flexShrink: 0,
              transition: "background 0.2s",
            }}
            onMouseEnter={(e) => {
              (e.currentTarget as HTMLButtonElement).style.backgroundColor = "rgba(0,0,0,0.06)";
            }}
            onMouseLeave={(e) => {
              (e.currentTarget as HTMLButtonElement).style.backgroundColor = "transparent";
            }}
          >
            {/* Chevron icon — rotates to face right when collapsed */}
            <svg
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="none"
              stroke="#555"
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round"
              style={{
                transform: isCollapsed ? "rotate(180deg)" : "rotate(0deg)",
                transition: "transform 0.25s cubic-bezier(0.4, 0, 0.2, 1)",
                display: "block",
              }}
            >
              <polyline points="15 18 9 12 15 6" />
            </svg>
          </button>
        </Tooltip>
      </GlobalSidebarHeaderContainer>

      {/* Sidebar rows */}
      {sidebarOptions?.map(({ label, value }) => {
        const isSelected = Boolean(value === String(selectedValue));

        return (
          <Tooltip
            key={value}
            title={isCollapsed ? label : ""}
            placement="right"
            arrow
          >
            <GlobalSidebarRowContainer
              isSelected={isSelected}
              isCollapsed={isCollapsed}
              onClick={() => { handeleSidebarClick(value) }}
            >
              <GlobalSidebarIconTextContainer>
                <SidebarIconRenderer
                  iconkey={value}
                  isSelected={isSelected}
                  isCollapsed={isCollapsed}
                />
                {!isCollapsed && label}
              </GlobalSidebarIconTextContainer>
              {!isCollapsed && value !== "submissions" && getCountBasedOnValue(value)}
            </GlobalSidebarRowContainer>
          </Tooltip>
        );
      })}
    </GlobalSidebarRootContainer>
  );
}

export default memo(GlobalSidebar);
